import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, type CrmUser, type Me, type Supplier, type SupplierQuestion } from './api';
import { ORTAK, rolAdi, tamSayi, tarih, TANIMSIZ } from './metin/ortak';
import { TEDARIKCI_DURUMU, TEDARIKCI_TURU, YONETIM_METIN } from './metin/yonetim';

/**
 * CRM-A13: tedarikçiler ve kullanıcı yönetimi.
 *
 * İki bölüm, iki ayrı izin. Sekmeyi gizlemek yetkilendirme değil: arkadaki her uç sunucuda
 * yeniden kontrol ediliyor ve izinsiz istek 403 dönüyor. Buradaki gösterme/gizleme yalnız
 * kullanılamayacak bir düğmeyi göstermemek için.
 *
 * Ekranda çizilen her cümle `metin/yonetim.ts` içinde; kod değerlerinin karşılıkları
 * `metin/ortak.ts` içinde.
 */

// Uçtaki `ROLE_NAMES` ile aynı liste olmak zorunda. 'HR' 30.08.2026'da eklendi: beşinci bölümü
// kadrolayan rol, ve buraya girmezse kimseye atanamaz.
const ROLES = ['ADMIN', 'MANAGER', 'CRM_SALES', 'OPERATIONS', 'FINANCE', 'MARKETING', 'HR', 'READ_ONLY'] as const;

function errorText(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return caught.message;
  return fallback;
}

function SupplierSection() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [questions, setQuestions] = useState<SupplierQuestion[]>([]);
  const [manualOnly, setManualOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const metin = YONETIM_METIN.tedarikciler;

  const load = useCallback(async () => {
    setError(null);
    try {
      const [list, open] = await Promise.all([
        api.suppliers({ pageSize: 100, ...(manualOnly ? { workMode: 'MANUAL' } : {}) }),
        api.supplierQuestions({ status: 'OPEN' }),
      ]);
      setSuppliers(list.items);
      setQuestions(open.items);
    } catch (caught) {
      setError(errorText(caught, metin.hata));
    }
  }, [manualOnly, metin.hata]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section id="suppliers" className="content-section" data-testid="suppliers" aria-labelledby="suppliers-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="suppliers-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{tamSayi(suppliers.length)} {ORTAK.kayit}</span>
      </div>

      {error ? <div className="error callout" role="alert">{error}</div> : null}

      <div className="toolbar">
        <label>
          <input
            type="checkbox"
            data-testid="supplier-manual-filter"
            checked={manualOnly}
            onChange={(event) => setManualOnly(event.target.checked)}
          />{' '}
          {metin.yalnizElle}
        </label>
      </div>

      <div className="table-wrap">
        <table data-testid="supplier-table">
          <thead>
            <tr>
              <th>{metin.sutunTedarikci}</th>
              <th>{metin.sutunTur}</th>
              <th>{metin.sutunDurum}</th>
              <th>{metin.sutunCalisma}</th>
              <th>{metin.sutunMuhatap}</th>
              <th>{metin.sutunAcikSoru}</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((supplier) => (
              <tr key={supplier.id} data-testid="supplier-row">
                <td>
                  <strong>{supplier.name}</strong>
                  <small> {supplier.code}</small>
                  {supplier.requiresIpAllowlist ? <small> · {metin.ipListesi}</small> : null}
                </td>
                <td>{TEDARIKCI_TURU[supplier.kind] ?? supplier.kind}</td>
                <td>
                  {/* Sınıf adı kod değerinden geliyor; ekrandaki yazı karşılığı. */}
                  <span className={`state state-${supplier.status.toLowerCase()}`}>
                    {TEDARIKCI_DURUMU[supplier.status] ?? supplier.status}
                  </span>
                </td>
                <td data-testid={`supplier-mode-${supplier.code}`}>
                  {supplier.workMode === 'MANUAL' ? metin.calismaElle : metin.calismaApi}
                </td>
                <td>
                  {/* Boş bir hücre "bilgi yok" demiyor; "kimse girmemiş" diyor ve ikisi farklı. */}
                  {supplier.contactName ?? supplier.contactEmail ?? supplier.contactPhone ?? metin.muhatapYok}
                </td>
                <td>{tamSayi(supplier.openQuestions ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {suppliers.length === 0 ? (
          <div className="empty-state">
            <strong>{metin.bosBaslik}</strong>
            <span>{metin.bosTarif}</span>
          </div>
        ) : null}
      </div>

      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.sorularUstBaslik}</span>
          <h3>{metin.sorularBaslik}</h3>
        </div>
        <span className="count-badge">{tamSayi(questions.length)}</span>
      </div>
      <ul data-testid="supplier-questions">
        {questions.map((question) => (
          <li key={question.id} data-testid="supplier-question">
            <strong>{question.supplierName}</strong> · {question.question}
            <small>
              {' '}
              · {metin.soruSahibi} {question.ownerName}
              {question.ownerIsActive ? '' : ` ${metin.soruSahibiPasif}`}, {tarih(question.askedAt)}
            </small>
          </li>
        ))}
      </ul>
      {questions.length === 0 ? <p className="muted">{metin.soruYok}</p> : null}
    </section>
  );
}

/**
 * Onay kutusu. `window.prompt` yerine: sebep sorulmadan rol, durum ve parola eylemi yapılmıyor ve
 * bir `prompt` ne erişilebilir ne denetlenebilir, mobilde de kutu gibi değil sistem penceresi gibi
 * çıkıyor. Escape ve dışarı tıklama kapatır; odak sebep alanında açılır.
 */
function EylemKutusu(props: {
  baslik: string;
  uyari?: string;
  sebepGerek: boolean;
  onayEtiketi: string;
  onayla: (sebep: string) => Promise<void>;
  kapat: () => void;
}) {
  const metin = YONETIM_METIN.kullanicilar;
  const [sebep, setSebep] = useState('');
  const [yapiliyor, setYapiliyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  useEffect(() => {
    const dinle = (event: KeyboardEvent) => {
      if (event.key === 'Escape') props.kapat();
    };
    window.addEventListener('keydown', dinle);
    return () => window.removeEventListener('keydown', dinle);
  }, [props]);

  return (
    <div className="kutu-zemin" onMouseDown={(event) => { if (event.target === event.currentTarget) props.kapat(); }}>
      <form
        className="kutu"
        role="dialog"
        aria-modal="true"
        aria-label={props.baslik}
        data-testid="user-action-dialog"
        onSubmit={async (event) => {
          event.preventDefault();
          setYapiliyor(true);
          setHata(null);
          try {
            await props.onayla(sebep.trim());
          } catch (caught) {
            setHata(errorText(caught, metin.islemHatasi));
            setYapiliyor(false);
          }
        }}
      >
        <h3>{props.baslik}</h3>
        {props.uyari ? <p className="muted">{props.uyari}</p> : null}
        {props.sebepGerek ? (
          <label className="kutu-alan">
            {metin.sebepEtiketi}
            <input
              type="text"
              autoFocus
              required
              minLength={3}
              maxLength={300}
              data-testid="user-action-reason"
              value={sebep}
              onChange={(event) => setSebep(event.target.value)}
            />
            <small className="muted">{metin.sebepKural}</small>
          </label>
        ) : null}
        {hata ? <div className="error callout" role="alert" data-testid="user-action-error">{hata}</div> : null}
        <div className="kutu-dugmeler">
          <button type="button" onClick={props.kapat}>{metin.vazgec}</button>
          <button type="submit" className="primary" data-testid="user-action-confirm" disabled={yapiliyor}>{props.onayEtiketi}</button>
        </div>
      </form>
    </div>
  );
}

/** Bağlantı kutusu: bağlantı yalnız burada, bir kez gösterilir. */
function BaglantiKutusu(props: { baslik: string; link: { setupUrl: string; expiresAt: string }; kapat: () => void }) {
  const metin = YONETIM_METIN.kullanicilar;
  const [kopya, setKopya] = useState<string | null>(null);
  return (
    <div className="kutu-zemin">
      <div className="kutu" role="dialog" aria-modal="true" aria-label={props.baslik} data-testid="user-link-dialog">
        <h3>{props.baslik}</h3>
        <p className="muted">{metin.baglantiTarif}</p>
        <label className="kutu-alan">
          {metin.baglantiEtiketi}
          <input type="text" readOnly data-testid="user-link-value" value={props.link.setupUrl} onFocus={(event) => event.currentTarget.select()} />
        </label>
        <p className="muted" data-testid="user-link-expiry">{metin.baglantiSure(tarih(props.link.expiresAt))}</p>
        {kopya ? <p role="status" className="muted">{kopya}</p> : null}
        <div className="kutu-dugmeler">
          <button
            type="button"
            data-testid="user-link-copy"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(props.link.setupUrl);
                setKopya(metin.baglantiKopyalandi);
              } catch {
                setKopya(metin.baglantiKopyalanamadi);
              }
            }}
          >
            {metin.baglantiKopyala}
          </button>
          <button type="button" className="primary" data-testid="user-link-close" onClick={props.kapat}>{metin.tamam}</button>
        </div>
      </div>
    </div>
  );
}

type Eylem =
  | { tur: 'rol'; user: CrmUser; yeni: string }
  | { tur: 'durum'; user: CrmUser }
  | { tur: 'sifirla'; user: CrmUser }
  | { tur: 'oturum'; user: CrmUser };

function UserSection({ me }: { me: Me }) {
  const [users, setUsers] = useState<CrmUser[]>([]);
  // Rozet sayıları sunucunun `total` alanından: tabloda görünen satır sayısı değil, süzgeçe uyan
  // hesap sayısı ve (süzgeç varsa) süzgeçsiz toplam. İlk yanıt gelmeden rozet çizilmiyor, "0 hesap" yalan olurdu.
  const [sayim, setSayim] = useState<{ gosterilen: number; toplam: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [eylem, setEylem] = useState<Eylem | null>(null);
  const [baglanti, setBaglanti] = useState<{ baslik: string; setupUrl: string; expiresAt: string } | null>(null);
  const canAssign = me.permissions.includes('user:assign_role');
  const canWrite = me.permissions.includes('user:write');
  // Parola sıfırlama yalnız ADMIN'in (sunucuda `refusalForPasswordReset`). Düğme, tıklanınca
  // reddedilecekse çizilmiyor.
  const canReset = canWrite && me.role === 'ADMIN';
  const metin = YONETIM_METIN.kullanicilar;

  const [ara, setAra] = useState('');
  const [rolSuzgeci, setRolSuzgeci] = useState('');
  const [durumSuzgeci, setDurumSuzgeci] = useState('ALL');

  const [yeniEposta, setYeniEposta] = useState('');
  const [yeniAd, setYeniAd] = useState('');
  const [yeniRol, setYeniRol] = useState<string>('READ_ONLY');
  const [yeniAciliyor, setYeniAciliyor] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const suzgecVar = Boolean(ara.trim() || rolSuzgeci || durumSuzgeci !== 'ALL');
      const [liste, hepsi] = await Promise.all([
        api.users({
          pageSize: 100,
          ...(ara.trim() ? { search: ara.trim() } : {}),
          ...(rolSuzgeci ? { role: rolSuzgeci } : {}),
          ...(durumSuzgeci !== 'ALL' ? { status: durumSuzgeci } : {}),
        }),
        suzgecVar ? api.users({ pageSize: 1 }) : Promise.resolve(null),
      ]);
      setUsers(liste.items);
      setSayim({ gosterilen: liste.total, toplam: hepsi ? hepsi.total : liste.total });
    } catch (caught) {
      setError(errorText(caught, metin.hata));
    }
  }, [ara, rolSuzgeci, durumSuzgeci, metin.hata]);

  useEffect(() => {
    // Yazarken her tuşta istek gitmesin.
    const zamanlayici = window.setTimeout(() => void load(), ara ? 250 : 0);
    return () => window.clearTimeout(zamanlayici);
  }, [load, ara]);

  const bitir = async (done: string) => {
    setEylem(null);
    setError(null);
    setNote(done);
    await load();
  };

  /**
   * "Yeni kullanıcı" formunun gönderimi. Parola yazılmıyor: sunucu davet bağlantısı üretiyor ve
   * kullanıcı parolasını onunla koyuyor. DTO ve `refusalForCreate` kuralları burada tekrarlanmıyor,
   * sunucunun reddettiği cümle ekranda kalıyor.
   */
  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setNote(null);
    setYeniAciliyor(true);
    try {
      const created = await api.createUser({ email: yeniEposta, displayName: yeniAd, role: yeniRol });
      setNote(metin.yeniAcildi(created.displayName));
      setBaglanti({ baslik: metin.baglantiBaslikDavet(created.displayName), setupUrl: created.setupUrl, expiresAt: created.setupExpiresAt });
      setYeniEposta('');
      setYeniAd('');
      setYeniRol('READ_ONLY');
      await load();
    } catch (caught) {
      setError(errorText(caught, metin.yeniAcHatasi));
    } finally {
      setYeniAciliyor(false);
    }
  };

  const eylemKutusu = () => {
    if (!eylem) return null;
    const { user } = eylem;
    if (eylem.tur === 'rol') {
      const yeni = eylem.yeni;
      return (
        <EylemKutusu
          baslik={metin.rolKutusuBaslik(user.displayName, rolAdi(user.role), rolAdi(yeni))}
          uyari={metin.rolKutusuUyari}
          sebepGerek
          onayEtiketi={metin.onayla}
          kapat={() => setEylem(null)}
          onayla={async (reason) => {
            await api.assignRole(user.id, { role: yeni, reason });
            await bitir(`${user.displayName}: ${rolAdi(user.role)} → ${rolAdi(yeni)}`);
          }}
        />
      );
    }
    if (eylem.tur === 'durum') {
      return (
        <EylemKutusu
          baslik={user.isActive ? metin.durumKutusuKapat(user.displayName) : metin.durumKutusuAc(user.displayName)}
          {...(user.isActive ? { uyari: metin.durumKutusuUyari } : {})}
          sebepGerek
          onayEtiketi={user.isActive ? metin.kapat : metin.geriAc}
          kapat={() => setEylem(null)}
          onayla={async (reason) => {
            await api.setUserStatus(user.id, { isActive: !user.isActive, reason });
            await bitir(`${user.displayName}: ${user.isActive ? metin.kapatildi : metin.geriAcildi}`);
          }}
        />
      );
    }
    if (eylem.tur === 'sifirla') {
      return (
        <EylemKutusu
          baslik={metin.sifirlaKutusuBaslik(user.displayName)}
          uyari={metin.sifirlaKutusuUyari}
          sebepGerek
          onayEtiketi={metin.onayla}
          kapat={() => setEylem(null)}
          onayla={async (reason) => {
            const sonuc = await api.resetUserPassword(user.id, { reason });
            setBaglanti({
              baslik: sonuc.purpose === 'INVITE' ? metin.baglantiBaslikDavet(user.displayName) : metin.baglantiBaslikSifirla(user.displayName),
              setupUrl: sonuc.setupUrl,
              expiresAt: sonuc.expiresAt,
            });
            await bitir(metin.sifirlandiNot(user.displayName, sonuc.revokedSessions));
          }}
        />
      );
    }
    return (
      <EylemKutusu
        baslik={metin.oturumKutusuBaslik(user.displayName)}
        sebepGerek={false}
        onayEtiketi={metin.onayla}
        kapat={() => setEylem(null)}
        onayla={async () => {
          const sonuc = await api.revokeUserSessions(user.id);
          await bitir(metin.oturumlarKapandi(user.displayName, sonuc.revokedSessions));
        }}
      />
    );
  };

  return (
    <section id="users" className="content-section" data-testid="users" aria-labelledby="users-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="users-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        {sayim ? (
          <span className="count-badge" data-testid="user-count">
            {sayim.gosterilen === sayim.toplam
              ? `${tamSayi(sayim.toplam)} ${metin.hesapEki}`
              : metin.hesaptanSayi(tamSayi(sayim.gosterilen), tamSayi(sayim.toplam))}
          </span>
        ) : null}
      </div>

      {error ? <div className="error callout" role="alert" data-testid="user-error">{error}</div> : null}
      {note ? <div className="callout callout-ok" role="status" data-testid="user-note">{note}</div> : null}

      {canWrite ? (
        <form className="user-create" data-testid="user-create-form" onSubmit={handleCreate}>
          <h3>{metin.yeniBaslik}</h3>
          <p className="muted">{metin.yeniAcTarif}</p>
          <div className="user-create-fields">
            <label>
              {metin.yeniAdEtiketi}
              <input type="text" required minLength={2} maxLength={120} data-testid="user-create-name" value={yeniAd} onChange={(event) => setYeniAd(event.target.value)} />
            </label>
            <label>
              {metin.yeniEpostaEtiketi}
              <input type="email" required maxLength={320} data-testid="user-create-email" value={yeniEposta} onChange={(event) => setYeniEposta(event.target.value)} />
            </label>
            <label>
              {metin.yeniRolEtiketi}
              <select data-testid="user-create-role" value={yeniRol} onChange={(event) => setYeniRol(event.target.value)}>
                {ROLES.map((role) => (
                  <option key={role} value={role}>{rolAdi(role)}</option>
                ))}
              </select>
            </label>
            <button type="submit" className="primary" data-testid="user-create-submit" disabled={yeniAciliyor}>
              {yeniAciliyor ? metin.yeniAciliyor : metin.yeniAc}
            </button>
          </div>
        </form>
      ) : null}

      <div className="toolbar user-filters" data-testid="user-filters">
        <label>
          <span className="sr-only">{metin.araEtiketi}</span>
          <input type="search" placeholder={metin.araYer} data-testid="user-search" value={ara} onChange={(event) => setAra(event.target.value)} />
        </label>
        <label>
          <span className="sr-only">{metin.rolSuzgeci}</span>
          <select data-testid="user-filter-role" aria-label={metin.rolSuzgeci} value={rolSuzgeci} onChange={(event) => setRolSuzgeci(event.target.value)}>
            <option value="">{metin.tumRoller}</option>
            {ROLES.map((role) => (
              <option key={role} value={role}>{rolAdi(role)}</option>
            ))}
          </select>
        </label>
        <label>
          <span className="sr-only">{metin.durumSuzgeci}</span>
          <select data-testid="user-filter-status" aria-label={metin.durumSuzgeci} value={durumSuzgeci} onChange={(event) => setDurumSuzgeci(event.target.value)}>
            <option value="ALL">{metin.durumHepsi}</option>
            <option value="ACTIVE">{metin.durumYalnizEtkin}</option>
            <option value="INACTIVE">{metin.durumYalnizPasif}</option>
          </select>
        </label>
      </div>

      <div className="table-wrap">
        <table data-testid="user-table" className="user-table">
          <thead>
            <tr>
              <th>{metin.sutunKullanici}</th>
              <th>{metin.sutunRol}</th>
              <th>{metin.sutunDurum}</th>
              <th>{metin.sutunSonGiris}</th>
              <th>{metin.sutunOturum}</th>
              {canWrite ? <th>{metin.sutunIslem}</th> : null}
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const self = user.id === me.userId;
              /*
               * Sunucunun kuralının ekrandaki karşılığı: ADMIN ya da MANAGER taşıyan bir kaydı
               * yalnız ADMIN düzenleyebiliyor (`user.rules.ts`, `ELEVATED_TARGET_REQUIRES_ADMIN`).
               * Tıklanınca reddedilecek bir kutu göstermek yalan olur; bu satırda çizilmiyor.
               */
              const yetkiliHedef = (user.role === 'ADMIN' || user.role === 'MANAGER') && me.role !== 'ADMIN';
              const duzenlenemez = self || yetkiliHedef;
              const bekleyen = user.setupPending ?? null;
              return (
                <tr key={user.id} data-testid="user-row">
                  <td data-label={metin.sutunKullanici}>
                    <strong>{user.displayName}</strong>{self ? <small className="muted"> ({metin.benim})</small> : null}
                    <br />
                    <small className="muted">{user.email}</small>
                  </td>
                  <td data-label={metin.sutunRol}>
                    {canAssign && !duzenlenemez ? (
                      <select
                        data-testid={`user-role-${user.id}`}
                        aria-label={metin.rolSeciciEtiketi(user.displayName)}
                        value={user.role}
                        onChange={(event) => {
                          const yeni = event.target.value;
                          if (yeni !== user.role) setEylem({ tur: 'rol', user, yeni });
                        }}
                      >
                        {ROLES.map((role) => (
                          <option key={role} value={role}>{rolAdi(role)}</option>
                        ))}
                      </select>
                    ) : (
                      <span>{rolAdi(user.role)}</span>
                    )}
                  </td>
                  <td data-label={metin.sutunDurum}>
                    <span className={`state state-${user.isActive ? 'active' : 'inactive'}`}>
                      {user.isActive ? metin.durumEtkin : metin.durumPasif}
                    </span>
                    {user.isActive && bekleyen ? (
                      <small className="muted" data-testid={`user-pending-${user.id}`}> {bekleyen === 'INVITE' ? metin.durumBekliyorDavet : metin.durumBekliyor}</small>
                    ) : null}
                  </td>
                  <td data-label={metin.sutunSonGiris}>{user.lastLoginAt ? tarih(user.lastLoginAt) : metin.hicGiris}</td>
                  <td data-label={metin.sutunOturum}>{tamSayi(user.openSessions ?? 0)}</td>
                  {canWrite ? (
                    <td data-label={metin.sutunIslem}>
                      {self ? (
                        <small className="muted">{metin.islemYok}</small>
                      ) : yetkiliHedef ? (
                        <span className="muted">{TANIMSIZ}</span>
                      ) : (
                        <div className="user-actions">
                          {canReset && user.isActive ? (
                            <button type="button" data-testid={`user-reset-${user.id}`} onClick={() => setEylem({ tur: 'sifirla', user })}>
                              {user.lastLoginAt ? metin.parolaSifirla : metin.davetYenile}
                            </button>
                          ) : null}
                          {(user.openSessions ?? 0) > 0 ? (
                            <button type="button" data-testid={`user-revoke-${user.id}`} onClick={() => setEylem({ tur: 'oturum', user })}>
                              {metin.oturumlariKapat}
                            </button>
                          ) : null}
                          <button type="button" data-testid={`user-status-${user.id}`} onClick={() => setEylem({ tur: 'durum', user })}>
                            {user.isActive ? metin.kapat : metin.geriAc}
                          </button>
                        </div>
                      )}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
        {users.length === 0 ? (
          <div className="empty-state"><strong>{metin.sonucYok}</strong></div>
        ) : null}
      </div>
      {eylemKutusu()}
      {baglanti ? <BaglantiKutusu baslik={baglanti.baslik} link={baglanti} kapat={() => setBaglanti(null)} /> : null}
    </section>
  );
}

/**
 * Tedarikçiler ve Kullanıcılar artık ayrı sayfalar, o yüzden ayrı ayrı dışa veriliyorlar.
 *
 * İzin denetimi **panelin kendisinde** duruyor, çağıranda değil: sayfa açılsa bile izin yoksa
 * panel çizilmiyor. Sekmeyi gizlemek yetkilendirme değil ve hiç olmadı; arkadaki her uç
 * sunucuda yeniden kontrol ediyor. Burada gizlenen şey yalnız kullanılamayacak bir düğme.
 */
export function SuppliersPage({ me }: { me: Me }) {
  return me.permissions.includes('supplier:read') ? <SupplierSection /> : null;
}

export function UsersPage({ me }: { me: Me }) {
  return me.permissions.includes('user:read') ? <UserSection me={me} /> : null;
}
